import { z } from 'zod';

/** A Torn player id. Torn uses 32-bit signed ints for user ids. */
export const PlayerId = z.coerce
  .number({ invalid_type_error: 'player id must be a number' })
  .int('player id must be a whole number')
  .positive('player id must be positive')
  .max(2_147_483_647, 'player id is too large');
export type PlayerId = z.infer<typeof PlayerId>;

/**
 * Our normalized view of a player's public profile. This is the platform's own
 * shape — deliberately decoupled from Torn's raw API response so a Torn API change
 * only touches the mapping code, not every client.
 */
export const PlayerProfile = z.object({
  id: z.number().int(),
  name: z.string(),
  level: z.number().int(),
  rank: z.string().nullable(),
  title: z.string().nullable(),
  gender: z.string().nullable(),
  ageDays: z.number().int().nullable(),
  signedUp: z.number().int().nullable(),
  factionId: z.number().int().nullable(),
  awards: z.number().int().nullable(),
  friends: z.number().int().nullable(),
  enemies: z.number().int().nullable(),
  karma: z.number().int().nullable(),
  forumPosts: z.number().int().nullable(),
  role: z.string().nullable(),
  donator: z.string().nullable(),
  revivable: z.boolean().nullable(),
  life: z.object({ current: z.number().int(), maximum: z.number().int() }).nullable(),
  status: z.object({
    state: z.string(),
    description: z.string(),
    details: z.string().nullable(),
    color: z.string(),
    until: z.number().int().nullable(),
  }),
  lastAction: z
    .object({
      status: z.string(),
      timestamp: z.number().int(),
      relative: z.string(),
    })
    .nullable(),
  spouse: z
    .object({ id: z.number().int(), name: z.string(), status: z.string() })
    .nullable(),
  property: z.object({ id: z.number().int(), name: z.string() }).nullable(),
});
export type PlayerProfile = z.infer<typeof PlayerProfile>;
